import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/notifications?limit=20");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000 + Math.floor(Math.random() * 10_000));
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/support/notifications/mark-read", {}); setUnread(0); } catch { /* ignore */ }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative p-1.5 rounded-sm hover:bg-secondary" title="Notifications" data-testid="notification-bell">
        <Bell size={15} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-rose-600 text-white text-[9px] flex items-center justify-center" data-testid="notification-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-80 max-h-96 overflow-y-auto border border-border rounded-sm bg-card shadow-xl" data-testid="notification-dropdown">
          <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Notifications</div>
          {items.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">No notifications yet.</div>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id} className={`px-3 py-2 border-b border-border last:border-0 text-[11px] ${n.read ? "" : "bg-sky-50/60"}`} data-testid={`notification-${n.id}`}>
                  <div className="font-medium">{n.title}</div>
                  <div className="text-muted-foreground">{n.body}</div>
                  <div className="text-[9px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
